import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import AdminShell from "@/components/admin/AdminShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  FileText,
  FilePlus,
  Trash2,
  Plus,
  Download,
  Send,
  CheckCircle2,
  RefreshCw,
  ArrowRight,
  Receipt,
  Users,
  Settings as SettingsIcon,
  Eye,
  Loader2,
  Euro,
  TrendingUp,
  AlertCircle,
} from "lucide-react";

const STATUS_LABEL = {
  draft: "Entwurf",
  sent: "Versendet",
  paid: "Bezahlt",
  overdue: "Überfällig",
  cancelled: "Storniert",
  accepted: "Angenommen",
  declined: "Abgelehnt",
};
const STATUS_COLORS = {
  draft: "bg-neutral-200 text-neutral-700",
  sent: "bg-sky-100 text-sky-800",
  paid: "bg-emerald-100 text-emerald-800",
  overdue: "bg-rose-100 text-rose-800",
  cancelled: "bg-neutral-200 text-neutral-500",
  accepted: "bg-emerald-100 text-emerald-800",
  declined: "bg-rose-100 text-rose-800",
};

const fmtMoney = (n, ccy = "EUR") =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: ccy,
    maximumFractionDigits: 2,
  }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE");
  } catch {
    return iso;
  }
};

const todayISO = () => new Date().toISOString();
const inDaysISO = (days) =>
  new Date(Date.now() + days * 24 * 3600 * 1000).toISOString();

// =====================================================================
// MAIN PAGE
// =====================================================================
const AdminBilling = () => {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "dashboard";
  const setTab = (val) => setParams((p) => {
    p.set("tab", val);
    return p;
  });

  const [stats, setStats] = useState(null);
  const [docs, setDocs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // {docId} | {new:'invoice'|'quote'} | null
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, d, c, st] = await Promise.all([
        api.get("/admin/billing/stats").then((r) => r.data),
        api.get("/admin/billing/documents").then((r) => r.data),
        api.get("/admin/billing/customers").then((r) => r.data),
        api.get("/admin/billing/settings").then((r) => r.data),
      ]);
      setStats(s);
      setDocs(d);
      setCustomers(c);
      setSettings(st);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const newDoc = (type) => setEditor({ new: type });
  const openDoc = (id) => setEditor({ docId: id });

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/billing/documents/${confirmDelete.id}`);
      toast.success("Dokument gelöscht");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <AdminShell
      title="Rechnungen & Angebote"
      subtitle="PDF im Webseiten-Stil · automatische Nummerierung · per E-Mail versenden"
      actions={
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => newDoc("quote")}
            data-testid="billing-new-quote"
            className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm hover:bg-neutral-50"
          >
            <FilePlus className="w-4 h-4" /> Neues Angebot
          </button>
          <button
            onClick={() => newDoc("invoice")}
            data-testid="billing-new-invoice"
            className="inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-4 py-2 text-sm hover:bg-black"
          >
            <Plus className="w-4 h-4" /> Neue Rechnung
          </button>
        </div>
      }
    >
      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList data-testid="billing-tabs">
          <TabsTrigger value="dashboard" data-testid="billing-tab-dashboard">
            <TrendingUp className="w-4 h-4 mr-1.5" /> Übersicht
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="billing-tab-invoices">
            <Receipt className="w-4 h-4 mr-1.5" /> Rechnungen
          </TabsTrigger>
          <TabsTrigger value="quotes" data-testid="billing-tab-quotes">
            <FileText className="w-4 h-4 mr-1.5" /> Angebote
          </TabsTrigger>
          <TabsTrigger value="customers" data-testid="billing-tab-customers">
            <Users className="w-4 h-4 mr-1.5" /> Kunden
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="billing-tab-settings">
            <SettingsIcon className="w-4 h-4 mr-1.5" /> Stammdaten
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <DashboardPanel stats={stats} docs={docs} loading={loading} onOpen={openDoc} />
        </TabsContent>
        <TabsContent value="invoices">
          <DocsTable
            docs={docs.filter((d) => d.type === "invoice")}
            type="invoice"
            loading={loading}
            onOpen={openDoc}
            onDelete={(d) => setConfirmDelete({ id: d.id, label: d.number || "Entwurf" })}
            onNew={() => newDoc("invoice")}
          />
        </TabsContent>
        <TabsContent value="quotes">
          <DocsTable
            docs={docs.filter((d) => d.type === "quote")}
            type="quote"
            loading={loading}
            onOpen={openDoc}
            onDelete={(d) => setConfirmDelete({ id: d.id, label: d.number || "Entwurf" })}
            onNew={() => newDoc("quote")}
          />
        </TabsContent>
        <TabsContent value="customers">
          <CustomersPanel customers={customers} onChange={load} />
        </TabsContent>
        <TabsContent value="settings">
          <SettingsPanel settings={settings} onSaved={load} />
        </TabsContent>
      </Tabs>

      {editor && (
        <DocumentEditor
          mode={editor}
          customers={customers}
          settings={settings}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            load();
          }}
        />
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="billing-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Dokument löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              „{confirmDelete?.label}&quot; wird endgültig entfernt. Diese Aktion kann nicht
              rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="billing-delete-confirm-btn"
            >
              Endgültig löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
};

// =====================================================================
// Dashboard panel
// =====================================================================
const DashboardPanel = ({ stats, docs, loading, onOpen }) => {
  if (loading || !stats) {
    return <div className="py-20 grid place-items-center text-neutral-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }
  const t = stats.totals;
  const recent = [...docs].sort((a, b) => (b.created_at > a.created_at ? 1 : -1)).slice(0, 6);
  const maxMonthly = Math.max(...stats.monthly.map((m) => m.revenue), 1);
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Umsatz bezahlt" value={fmtMoney(t.revenue_paid)} icon={Euro} tone="emerald" />
        <StatCard label="Offene Forderungen" value={fmtMoney(t.outstanding)} icon={AlertCircle} tone="amber" />
        <StatCard label="Rechnungen" value={`${t.paid} / ${t.invoices}`} sub="bezahlt / gesamt" icon={Receipt} />
        <StatCard label="Angebot → Rechnung" value={`${t.conversion_rate} %`} sub={`${t.accepted_quotes} von ${t.quotes} Angeboten`} icon={TrendingUp} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-3xl bg-white border border-neutral-200 p-6">
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-4">Umsatz 12 Monate · bezahlt</div>
          <div className="h-48 flex items-end gap-1.5">
            {stats.monthly.map((m, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1.5 group">
                <div className="w-full bg-neutral-100 rounded-t-md relative overflow-hidden" style={{ height: "100%" }}>
                  <div
                    className="absolute inset-x-0 bottom-0 bg-sky-700 rounded-t-md group-hover:bg-sky-600 transition-colors"
                    style={{ height: `${(m.revenue / maxMonthly) * 100}%` }}
                    title={`${fmtMoney(m.revenue)} · ${m.count} Rechnungen`}
                  />
                </div>
                <div className="text-[10px] text-neutral-500">{m.month.slice(5)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl bg-white border border-neutral-200 p-6">
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-4">Top Kunden</div>
          {stats.top_customers.length === 0 ? (
            <div className="text-sm text-neutral-400">Noch keine Daten.</div>
          ) : (
            <div className="space-y-3">
              {stats.top_customers.map((c, i) => (
                <div key={i} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-xs text-neutral-500">{c.count} Rechnung{c.count !== 1 ? "en" : ""}</div>
                  </div>
                  <div className="text-sm font-medium tabular-nums">{fmtMoney(c.amount)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-neutral-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-100 text-[11px] uppercase tracking-wider text-neutral-500">
          Zuletzt erstellt
        </div>
        {recent.length === 0 ? (
          <div className="p-10 text-center text-neutral-500">
            Noch keine Rechnungen oder Angebote. Lege oben rechts dein erstes Dokument an.
          </div>
        ) : (
          <ul>
            {recent.map((d) => (
              <li
                key={d.id}
                onClick={() => onOpen(d.id)}
                className="px-6 py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-neutral-50 border-b border-neutral-100 last:border-0"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-xl grid place-items-center ${d.type === "invoice" ? "bg-sky-50 text-sky-700" : "bg-neutral-100 text-neutral-700"}`}>
                    {d.type === "invoice" ? <Receipt className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{d.number || "Entwurf"} · {d.customer_snapshot?.name || "—"}</div>
                    <div className="text-xs text-neutral-500">{fmtDate(d.created_at)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={`rounded-full ${STATUS_COLORS[d.status] || ""}`}>{STATUS_LABEL[d.status] || d.status}</Badge>
                  <div className="text-sm font-medium tabular-nums">{fmtMoney(d.totals?.total || 0)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sub, icon: Icon, tone }) => {
  const toneCls =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-700"
      : tone === "amber"
      ? "bg-amber-50 text-amber-700"
      : "bg-sky-50 text-sky-700";
  return (
    <div className="rounded-3xl bg-white border border-neutral-200 p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl grid place-items-center ${toneCls}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</div>
      </div>
      <div className="mt-3 font-display text-2xl font-light tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-neutral-500">{sub}</div>}
    </div>
  );
};

// =====================================================================
// Documents table
// =====================================================================
const DocsTable = ({ docs, type, loading, onOpen, onDelete, onNew }) => {
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(
    () => (filter === "all" ? docs : docs.filter((d) => d.status === filter)),
    [docs, filter]
  );
  const statuses =
    type === "invoice"
      ? ["all", "draft", "sent", "paid", "overdue", "cancelled"]
      : ["all", "draft", "sent", "accepted", "declined"];

  if (loading) return <div className="py-20 grid place-items-center text-neutral-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {statuses.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`billing-filter-${s}`}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
              filter === s ? "bg-neutral-950 text-white" : "bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50"
            }`}
          >
            {s === "all" ? "Alle" : STATUS_LABEL[s]}
            <span className="ml-1.5 text-[10px] opacity-60">
              {s === "all" ? docs.length : docs.filter((d) => d.status === s).length}
            </span>
          </button>
        ))}
      </div>

      <div className="rounded-3xl bg-white border border-neutral-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-neutral-500">
            <div className="font-medium text-neutral-800">Keine Einträge</div>
            <div className="mt-1 text-sm">Lege dein erstes {type === "invoice" ? "Rechnungs-" : "Angebots-"}Dokument an.</div>
            <button
              onClick={onNew}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-4 py-2 text-sm hover:bg-black"
              data-testid={`billing-empty-create-${type}`}
            >
              <Plus className="w-4 h-4" /> Neu erstellen
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 border-b border-neutral-100">
                <th className="px-5 py-3">Nummer</th>
                <th className="px-5 py-3">Kunde</th>
                <th className="px-5 py-3">Datum</th>
                <th className="px-5 py-3">Fällig / Gültig</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Betrag</th>
                <th className="px-5 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr
                  key={d.id}
                  data-testid={`billing-row-${d.id}`}
                  className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50 cursor-pointer group"
                  onClick={(e) => {
                    if (e.target.closest("button")) return;
                    onOpen(d.id);
                  }}
                >
                  <td className="px-5 py-3 font-medium">{d.number || <span className="text-neutral-400">Entwurf</span>}</td>
                  <td className="px-5 py-3">{d.customer_snapshot?.name || "—"}</td>
                  <td className="px-5 py-3 text-sm text-neutral-600">{fmtDate(d.issue_date)}</td>
                  <td className="px-5 py-3 text-sm text-neutral-600">
                    {fmtDate(d.due_date || d.valid_until)}
                  </td>
                  <td className="px-5 py-3">
                    <Badge className={`rounded-full ${STATUS_COLORS[d.status] || ""}`}>{STATUS_LABEL[d.status] || d.status}</Badge>
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-medium">{fmtMoney(d.totals?.total || 0, d.currency)}</td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => onDelete(d)}
                      data-testid={`billing-delete-${d.id}`}
                      className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center w-8 h-8 rounded-full text-neutral-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// =====================================================================
// Customers panel
// =====================================================================
const CustomersPanel = ({ customers, onChange }) => {
  const [editing, setEditing] = useState(null);
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setEditing({ name: "", country: "Spain" })}
          data-testid="billing-new-customer"
          className="inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-4 py-2 text-sm hover:bg-black"
        >
          <Plus className="w-4 h-4" /> Neuer Kunde
        </button>
      </div>
      <div className="rounded-3xl bg-white border border-neutral-200 overflow-hidden">
        {customers.length === 0 ? (
          <div className="p-12 text-center text-neutral-500">Noch keine Kunden angelegt.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 border-b border-neutral-100">
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Kontakt</th>
                <th className="px-5 py-3">E-Mail</th>
                <th className="px-5 py-3">Ort</th>
                <th className="px-5 py-3">USt-ID</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setEditing(c)}
                  data-testid={`billing-customer-row-${c.id}`}
                  className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50 cursor-pointer"
                >
                  <td className="px-5 py-3 font-medium">{c.name}</td>
                  <td className="px-5 py-3 text-sm">{c.contact_name || "—"}</td>
                  <td className="px-5 py-3 text-sm">{c.email || "—"}</td>
                  <td className="px-5 py-3 text-sm">{[c.postal_code, c.city, c.country].filter(Boolean).join(", ")}</td>
                  <td className="px-5 py-3 text-sm">{c.vat_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <CustomerDialog
        customer={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          onChange();
        }}
      />
    </div>
  );
};

const CustomerDialog = ({ customer, onClose, onSaved }) => {
  const [f, setF] = useState(customer);
  useEffect(() => setF(customer), [customer]);
  if (!customer) return null;
  const update = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      if (f.id) {
        await api.patch(`/admin/billing/customers/${f.id}`, f);
      } else {
        await api.post(`/admin/billing/customers`, f);
      }
      toast.success("Kunde gespeichert");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };
  const remove = async () => {
    if (!f.id) return;
    try {
      await api.delete(`/admin/billing/customers/${f.id}`);
      toast.success("Kunde entfernt");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };
  return (
    <Dialog open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="billing-customer-dialog">
        <DialogHeader>
          <DialogTitle>{customer.id ? "Kunde bearbeiten" : "Neuer Kunde"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <FieldText label="Firma / Name" value={f?.name} onChange={(v) => update("name", v)} testId="billing-customer-name" />
          <FieldText label="Ansprechpartner" value={f?.contact_name} onChange={(v) => update("contact_name", v)} />
          <FieldText label="E-Mail" value={f?.email} onChange={(v) => update("email", v)} testId="billing-customer-email" />
          <FieldText label="Telefon" value={f?.phone} onChange={(v) => update("phone", v)} />
          <FieldText label="Straße" value={f?.address_line1} onChange={(v) => update("address_line1", v)} />
          <FieldText label="Adresszusatz" value={f?.address_line2} onChange={(v) => update("address_line2", v)} />
          <FieldText label="PLZ" value={f?.postal_code} onChange={(v) => update("postal_code", v)} />
          <FieldText label="Ort" value={f?.city} onChange={(v) => update("city", v)} />
          <FieldText label="Land" value={f?.country} onChange={(v) => update("country", v)} />
          <FieldText label="USt-ID / VAT" value={f?.vat_id} onChange={(v) => update("vat_id", v)} />
        </div>
        <DialogFooter className="mt-3 flex-wrap gap-2 sm:justify-between">
          {f?.id ? (
            <button
              onClick={remove}
              className="text-red-600 text-sm hover:underline"
              data-testid="billing-customer-delete"
            >
              Kunde löschen
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm hover:bg-neutral-50">Abbrechen</button>
            <button onClick={save} className="rounded-full bg-neutral-950 text-white px-4 py-2 text-sm hover:bg-black" data-testid="billing-customer-save">Speichern</button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =====================================================================
// Settings panel
// =====================================================================
const SettingsPanel = ({ settings, onSaved }) => {
  const [f, setF] = useState(settings);
  useEffect(() => setF(settings), [settings]);
  if (!f) return <div className="py-20 grid place-items-center text-neutral-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const update = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      await api.put("/admin/billing/settings", f);
      toast.success("Stammdaten gespeichert");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };
  return (
    <div className="space-y-6">
      <Card title="Firmendaten (auf jeder Rechnung gedruckt)">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldText label="Firmenname" value={f.company_name} onChange={(v) => update("company_name", v)} testId="billing-settings-company" />
          <FieldText label="EIN / Tax-ID" value={f.tax_id} onChange={(v) => update("tax_id", v)} />
          <FieldText label="Adresse Zeile 1" value={f.address_line1} onChange={(v) => update("address_line1", v)} />
          <FieldText label="Adresse Zeile 2" value={f.address_line2} onChange={(v) => update("address_line2", v)} />
          <FieldText label="E-Mail" value={f.email} onChange={(v) => update("email", v)} />
          <FieldText label="Telefon" value={f.phone} onChange={(v) => update("phone", v)} />
          <FieldText label="Webseite" value={f.website} onChange={(v) => update("website", v)} />
        </div>
      </Card>
      <Card title="Bankverbindung">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <FieldText label="Bank" value={f.bank_name} onChange={(v) => update("bank_name", v)} />
          <FieldText label="IBAN" value={f.iban} onChange={(v) => update("iban", v)} testId="billing-settings-iban" />
          <FieldText label="BIC / SWIFT" value={f.bic} onChange={(v) => update("bic", v)} />
        </div>
      </Card>
      <Card title="Standardwerte">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <FieldText label="Standard-USt-Satz (%)" value={f.default_vat_rate} onChange={(v) => update("default_vat_rate", Number(v))} type="number" />
          <FieldText label="Standard Zahlungsziel (Tage)" value={f.default_payment_days} onChange={(v) => update("default_payment_days", Number(v))} type="number" />
          <FieldText label="Standard Angebots-Gültigkeit (Tage)" value={f.default_quote_validity_days} onChange={(v) => update("default_quote_validity_days", Number(v))} type="number" />
          <FieldText label="Rechnungs-Startnummer" value={f.invoice_start} onChange={(v) => update("invoice_start", Number(v))} type="number" />
          <FieldText label="Angebots-Startnummer" value={f.quote_start} onChange={(v) => update("quote_start", Number(v))} type="number" />
          <FieldText label="Währung" value={f.currency} onChange={(v) => update("currency", v)} />
        </div>
        <FieldText
          className="mt-3"
          label="Standard-Zahlungsbedingungen (auf der Rechnung)"
          value={f.payment_terms}
          onChange={(v) => update("payment_terms", v)}
        />
      </Card>
      <div className="flex justify-end">
        <button onClick={save} className="rounded-full bg-neutral-950 text-white px-5 py-2.5 text-sm hover:bg-black" data-testid="billing-settings-save">
          Stammdaten speichern
        </button>
      </div>
    </div>
  );
};

const Card = ({ title, children }) => (
  <div className="rounded-3xl bg-white border border-neutral-200 p-6">
    <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-4">{title}</div>
    {children}
  </div>
);

const FieldText = ({ label, value, onChange, type = "text", className = "", testId }) => (
  <div className={className}>
    <Label className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</Label>
    <Input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5"
      data-testid={testId}
    />
  </div>
);

// =====================================================================
// Document editor (modal)
// =====================================================================
const emptyItem = (vatRate = 21) => ({
  description: "",
  quantity: 1,
  unit: "Stk",
  unit_price: 0,
  vat_rate: vatRate,
});

const DocumentEditor = ({ mode, customers, settings, onClose, onSaved }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        if (mode.docId) {
          const { data } = await api.get(`/admin/billing/documents/${mode.docId}`);
          setDoc(data);
        } else {
          const type = mode.new;
          const fresh = {
            type,
            status: "draft",
            customer_id: null,
            customer_snapshot: null,
            issue_date: todayISO(),
            due_date: type === "invoice" ? inDaysISO(settings?.default_payment_days || 14) : null,
            valid_until: type === "quote" ? inDaysISO(settings?.default_quote_validity_days || 30) : null,
            items: [emptyItem(settings?.default_vat_rate || 21)],
            notes: "",
            payment_terms: "",
            currency: settings?.currency || "EUR",
          };
          const { data } = await api.post("/admin/billing/documents", fresh);
          setDoc(data);
        }
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [mode]);

  const save = async (patch = {}) => {
    const next = { ...doc, ...patch };
    setDoc(next);
    try {
      const { data } = await api.patch(`/admin/billing/documents/${doc.id}`, patch);
      setDoc(data);
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const updateItem = (idx, key, val) => {
    const items = [...doc.items];
    items[idx] = { ...items[idx], [key]: key === "description" || key === "unit" ? val : Number(val) || 0 };
    save({ items });
  };
  const addItem = () => save({ items: [...doc.items, emptyItem(settings?.default_vat_rate || 21)] });
  const removeItem = (idx) => save({ items: doc.items.filter((_, i) => i !== idx) });

  const totals = useMemo(() => {
    let subtotal = 0;
    const buckets = {};
    (doc?.items || []).forEach((it) => {
      const net = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
      const v = net * ((Number(it.vat_rate) || 0) / 100);
      subtotal += net;
      buckets[it.vat_rate] = (buckets[it.vat_rate] || 0) + v;
    });
    const vat = Object.values(buckets).reduce((s, v) => s + v, 0);
    return { subtotal, buckets, vat, total: subtotal + vat };
  }, [doc]);

  const issue = async () => {
    try {
      const { data } = await api.post(`/admin/billing/documents/${doc.id}/issue`);
      setDoc(data);
      toast.success(`Nummer ${data.number} vergeben.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const setStatus = async (status) => {
    try {
      const { data } = await api.post(`/admin/billing/documents/${doc.id}/status?status=${status}`);
      setDoc(data);
      toast.success(`Status: ${STATUS_LABEL[status] || status}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/admin/billing/documents/${doc.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.type}_${doc.number || "entwurf"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("PDF konnte nicht erstellt werden");
    }
  };

  const previewPdf = () => {
    const url = `${api.defaults.baseURL}/admin/billing/documents/${doc.id}/pdf`;
    window.open(url, "_blank", "noopener");
  };

  const sendEmail = async () => {
    if (!doc.customer_snapshot?.email) {
      toast.error("Der Kunde hat keine E-Mail-Adresse.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/billing/documents/${doc.id}/send-email?cc_self=true`);
      if (data.ok) {
        toast.success(`E-Mail an ${data.to} gesendet`);
        const { data: refreshed } = await api.get(`/admin/billing/documents/${doc.id}`);
        setDoc(refreshed);
      } else {
        toast.error("Versand fehlgeschlagen. Prüfe die SMTP-Einstellungen.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const convertToInvoice = async () => {
    try {
      const { data } = await api.post(`/admin/billing/documents/${doc.id}/convert`);
      toast.success(`Rechnung aus Angebot erstellt`);
      onSaved();
      // Re-open editor for the new invoice
      setTimeout(() => {
        const ev = new CustomEvent("billing:openDoc", { detail: data.id });
        window.dispatchEvent(ev);
      }, 100);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading || !doc) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl"><div className="py-12 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div></DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        className="max-w-5xl max-h-[92vh] overflow-y-auto"
        data-testid="billing-editor-dialog"
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-3 pr-8">
            <div>
              <DialogTitle className="text-2xl font-display tracking-tight">
                {doc.type === "invoice" ? "Rechnung" : "Angebot"}{" "}
                <span className="text-neutral-400 font-light">·</span>{" "}
                {doc.number || <span className="text-neutral-400">Entwurf</span>}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {doc.status === "draft"
                  ? "Entwurf – noch nicht versendet, Nummer wird beim Ausstellen vergeben."
                  : `Status: ${STATUS_LABEL[doc.status]}`}
              </DialogDescription>
            </div>
            <Badge className={`rounded-full ${STATUS_COLORS[doc.status] || ""}`}>{STATUS_LABEL[doc.status] || doc.status}</Badge>
          </div>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Customer + dates */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-1">
              <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Kunde</Label>
              <Select value={doc.customer_id || ""} onValueChange={(v) => save({ customer_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="billing-editor-customer-select">
                  <SelectValue placeholder="Kunde auswählen…" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {doc.customer_snapshot && (
                <div className="mt-2 text-xs text-neutral-500 leading-snug">
                  {doc.customer_snapshot.email || "Keine E-Mail"}
                  {doc.customer_snapshot.city && ` · ${doc.customer_snapshot.city}`}
                </div>
              )}
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Datum</Label>
              <Input
                type="date"
                value={doc.issue_date ? doc.issue_date.slice(0, 10) : ""}
                onChange={(e) => save({ issue_date: new Date(e.target.value).toISOString() })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-neutral-500">
                {doc.type === "invoice" ? "Fällig am" : "Gültig bis"}
              </Label>
              <Input
                type="date"
                value={(doc.type === "invoice" ? doc.due_date : doc.valid_until)?.slice(0, 10) || ""}
                onChange={(e) =>
                  save(
                    doc.type === "invoice"
                      ? { due_date: new Date(e.target.value).toISOString() }
                      : { valid_until: new Date(e.target.value).toISOString() }
                  )
                }
                className="mt-1.5"
              />
            </div>
          </div>

          {/* Items */}
          <div className="rounded-2xl border border-neutral-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-neutral-50">
                <tr className="text-[10px] uppercase tracking-wider text-neutral-500 text-left">
                  <th className="px-3 py-2 w-10">#</th>
                  <th className="px-3 py-2">Beschreibung</th>
                  <th className="px-3 py-2 w-20">Menge</th>
                  <th className="px-3 py-2 w-20">Einheit</th>
                  <th className="px-3 py-2 w-28">Einzelpreis</th>
                  <th className="px-3 py-2 w-20">IVA %</th>
                  <th className="px-3 py-2 w-28 text-right">Gesamt</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {doc.items.map((it, i) => {
                  const net = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                  return (
                    <tr key={i} className="border-t border-neutral-100" data-testid={`billing-item-row-${i}`}>
                      <td className="px-3 py-2 text-sm text-neutral-500">{i + 1}</td>
                      <td className="px-3 py-2">
                        <Textarea
                          rows={1}
                          value={it.description}
                          onChange={(e) => updateItem(i, "description", e.target.value)}
                          className="min-h-[36px] text-sm"
                          placeholder="Leistung beschreiben…"
                          data-testid={`billing-item-desc-${i}`}
                        />
                      </td>
                      <td className="px-3 py-2"><Input type="number" step="any" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="text-sm" /></td>
                      <td className="px-3 py-2"><Input value={it.unit} onChange={(e) => updateItem(i, "unit", e.target.value)} className="text-sm" /></td>
                      <td className="px-3 py-2"><Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="text-sm tabular-nums" /></td>
                      <td className="px-3 py-2">
                        <Select value={String(it.vat_rate)} onValueChange={(v) => updateItem(i, "vat_rate", v)}>
                          <SelectTrigger className="text-sm" data-testid={`billing-item-vat-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="21">21 %</SelectItem>
                            <SelectItem value="10">10 %</SelectItem>
                            <SelectItem value="7">7 %</SelectItem>
                            <SelectItem value="4">4 %</SelectItem>
                            <SelectItem value="0">0 % (Reverse)</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2 text-right text-sm tabular-nums font-medium">{fmtMoney(net, doc.currency)}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => removeItem(i)} className="text-neutral-400 hover:text-red-600" data-testid={`billing-item-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="border-t border-neutral-100 px-3 py-2 bg-neutral-50/50">
              <button onClick={addItem} className="inline-flex items-center gap-1.5 text-sm text-sky-700 hover:underline" data-testid="billing-add-item">
                <Plus className="w-4 h-4" /> Position hinzufügen
              </button>
            </div>
          </div>

          {/* Totals */}
          <div className="flex flex-col items-end gap-1 text-sm">
            <div className="flex justify-between w-72">
              <span className="text-neutral-500">Zwischensumme</span>
              <span className="tabular-nums">{fmtMoney(totals.subtotal, doc.currency)}</span>
            </div>
            {Object.entries(totals.buckets).map(([rate, amt]) => (
              <div key={rate} className="flex justify-between w-72">
                <span className="text-neutral-500">IVA {rate} %</span>
                <span className="tabular-nums">{fmtMoney(amt, doc.currency)}</span>
              </div>
            ))}
            <div className="flex justify-between w-72 mt-1 pt-2 border-t border-neutral-200 font-medium text-base">
              <span>Gesamtbetrag</span>
              <span className="tabular-nums" data-testid="billing-editor-total">{fmtMoney(totals.total, doc.currency)}</span>
            </div>
          </div>

          {/* Notes */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Anmerkungen (im PDF)</Label>
              <Textarea
                rows={4}
                value={doc.notes || ""}
                onChange={(e) => setDoc((p) => ({ ...p, notes: e.target.value }))}
                onBlur={() => save({ notes: doc.notes })}
                placeholder="Optionaler Text auf dem Dokument…"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Zahlungsbedingungen (überschreibt Standard)</Label>
              <Textarea
                rows={4}
                value={doc.payment_terms || ""}
                onChange={(e) => setDoc((p) => ({ ...p, payment_terms: e.target.value }))}
                onBlur={() => save({ payment_terms: doc.payment_terms })}
                placeholder={settings?.payment_terms || ""}
                className="mt-1.5"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="mt-5 sm:justify-between gap-2 flex-wrap">
          <button
            onClick={previewPdf}
            data-testid="billing-editor-preview"
            className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm hover:bg-neutral-50"
          >
            <Eye className="w-4 h-4" /> PDF-Vorschau
          </button>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={downloadPdf}
              data-testid="billing-editor-download"
              className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm hover:bg-neutral-50"
            >
              <Download className="w-4 h-4" /> Download
            </button>
            {!doc.number && (
              <button
                onClick={issue}
                data-testid="billing-editor-issue"
                className="inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-4 py-2 text-sm hover:bg-black"
              >
                <CheckCircle2 className="w-4 h-4" /> Nummer vergeben & ausstellen
              </button>
            )}
            <button
              onClick={sendEmail}
              disabled={sending || !doc.customer_snapshot?.email}
              data-testid="billing-editor-send"
              className="inline-flex items-center gap-2 rounded-full bg-sky-700 text-white px-4 py-2 text-sm hover:bg-sky-800 disabled:opacity-50"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              An Kunden senden
            </button>
            {doc.type === "invoice" && doc.status !== "paid" && (
              <button
                onClick={() => setStatus("paid")}
                data-testid="billing-editor-mark-paid"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-4 py-2 text-sm hover:bg-emerald-700"
              >
                <CheckCircle2 className="w-4 h-4" /> Als bezahlt markieren
              </button>
            )}
            {doc.type === "quote" && !doc.converted_to && (
              <button
                onClick={convertToInvoice}
                data-testid="billing-editor-convert"
                className="inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-4 py-2 text-sm hover:bg-black"
              >
                <ArrowRight className="w-4 h-4" /> In Rechnung umwandeln
              </button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminBilling;
